import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';

import BlueGroupIcon from '../icons/BlueGroupIcon';
import OrangeGroupIcon from '../icons/OrangeGroupIcon';
import GroupActionsMenu from './GroupActionsMenu';
import { CARD, KICKER, PILL_PRIMARY, PILL_SECONDARY, cx } from '../../pages/Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS, REDESIGN_TINTS } from '../../constants';

const useStyles = makeStyles(() => ({
    card: {
        ...CARD,
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        padding: '22px',
    },
    // A group past the plan's limit: the frame goes amber and a chip says why.
    locked: {
        borderColor: REDESIGN_COLORS.amberBorderStrong,
        background: REDESIGN_COLORS.amberSurface,
    },
    top: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
    },
    disc: {
        display: 'grid',
        placeItems: 'center',
        width: '48px',
        height: '48px',
        borderRadius: '16px',
    },
    discMine: {
        background: REDESIGN_COLORS.mist,
    },
    discGuest: {
        background: REDESIGN_TINTS.peach,
    },
    groupIcon: {
        width: '28px',
        height: '16px',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '6px',
    },
    chip: {
        display: 'inline-block',
        padding: '4px 9px',
        borderRadius: '999px',
        fontSize: '0.68rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    chipMine: {
        color: REDESIGN_COLORS.link,
        background: REDESIGN_COLORS.mist,
    },
    chipGuest: {
        color: REDESIGN_COLORS.accent,
        background: REDESIGN_TINTS.peach,
    },
    chipRequests: {
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.amberBadge,
    },
    chipLocked: {
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.amberBadge,
    },
    name: {
        margin: 0,
        fontSize: '1.15rem',
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        overflowWrap: 'anywhere',
    },
    owner: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    avatar: {
        width: '28px',
        height: '28px',
        fontSize: '0.8rem',
        background: REDESIGN_COLORS.line,
        color: REDESIGN_COLORS.dark,
    },
    kicker: {
        ...KICKER,
        marginBottom: '3px',
    },
    date: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.88rem',
    },
    actions: {
        marginTop: 'auto',
        paddingTop: '4px',
    },
    start: {
        ...PILL_PRIMARY,
        width: '100%',
    },
    join: {
        ...PILL_SECONDARY,
        width: '100%',
    },
}));

export const ownerName = (row) => [row.first_name, row.last_name].filter(Boolean).join(' ');

/** The date options both the card and the list row format the last access with. */
export const LAST_ACCESS_FORMAT = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };

/**
 * One group as a card: who owns it, when it was last used, and its one main
 * action (start the lesson for mine, join it for theirs). `actions` adds the
 * menu of secondary actions. `row` is a group as `loadClassesHook` returns
 * it, with `isMine` and `enabled` already set.
 */
const GroupCard = ({ row, profile, onStart, actions = [] }) => {
    const classes = useStyles();
    const intl = useIntl();

    const mine = !!row.isMine;
    const targetId = mine ? row.id : row.classId;
    const requests = mine ? Number(row.requests) || 0 : 0;
    const owner = mine ? profile.name : ownerName(row);
    const avatar = mine ? profile.avatar : row.avatar;
    const locked = row.enabled === false;

    return (
        <article className={cx(classes.card, locked && classes.locked)}>
            <div className={classes.top}>
                <span aria-hidden="true" className={cx(classes.disc, mine ? classes.discMine : classes.discGuest)}>
                    {mine ? <BlueGroupIcon className={classes.groupIcon} /> : <OrangeGroupIcon className={classes.groupIcon} />}
                </span>
                <span className={classes.chips}>
                    {locked ? (
                        <span className={cx(classes.chip, classes.chipLocked)}>
                            <FormattedMessage id="redesign.groups.locked" />
                        </span>
                    ) : null}
                    {requests > 0 ? (
                        <span className={cx(classes.chip, classes.chipRequests)}>
                            <FormattedMessage id="redesign.home.dashboard.recent.requests" values={{ count: requests }} />
                        </span>
                    ) : null}
                    <span className={cx(classes.chip, mine ? classes.chipMine : classes.chipGuest)}>
                        <FormattedMessage
                            id={mine ? 'redesign.home.dashboard.recent.mine' : 'redesign.home.dashboard.recent.guest'}
                        />
                    </span>
                    <GroupActionsMenu row={row} actions={actions} />
                </span>
            </div>

            <h3 className={classes.name}>{row.name}</h3>

            <div className={classes.owner}>
                <Avatar src={avatar || undefined} alt="" className={classes.avatar}>
                    {(owner || '?').charAt(0).toUpperCase()}
                </Avatar>
                <span>{owner}</span>
            </div>

            <div>
                <span className={classes.kicker}>
                    <FormattedMessage id="redesign.generic.label.lastAccessed" />
                </span>
                <p className={classes.date}>{intl.formatDate(row.lastaccess || row.updatedAt, LAST_ACCESS_FORMAT)}</p>
            </div>

            <div className={classes.actions}>
                <button type="button" onClick={() => onStart(targetId)} className={mine ? classes.start : classes.join}>
                    <FormattedMessage id={mine ? 'redesign.generic.label.start' : 'generic.label.join'} />
                    <span aria-hidden="true">→</span>
                </button>
            </div>
        </article>
    );
};

export default GroupCard;
